import type { ThemeConfig } from 'antd';
import { theme as antdTheme } from 'antd';

import { isInWebView } from '@/shared/webview/env';

// Значения синхронизированы с CSS-переменными в src/styles/theme.scss, где
// палитры так же разведены по `.is-webview`: в браузере бренд фиолетовый,
// внутри супер-аппа — оранжевый.

/** Вебка (браузер/PWA): фиолетовый бренд, тон не меняется между темами. */
const WEB_PALETTE = {
  colorPrimary: '#7c5cff',
  colorError: '#e5484d',
  colorSuccess: '#1fb87a',
  colorWarning: '#f2a93b',
  colorInfo: '#2b8fe5'
};

/** Нативка (webview): оранжевый бренд приглушён в тёмной теме, статусы светлее. */
const WEBVIEW_PALETTE = {
  light: {
    colorPrimary: '#ff7726',
    colorLink: '#b24d0e',
    colorError: '#e5484d',
    colorSuccess: '#17715a',
    colorWarning: '#e5a83a',
    colorInfo: '#176fbd',
    colorText: '#10192a'
  },
  dark: {
    colorPrimary: '#f27b36',
    colorLink: '#f27b36',
    colorError: '#ef6468',
    colorSuccess: '#3fb58a',
    colorWarning: '#e5a83a',
    colorInfo: '#4aa3ef',
    colorText: '#f1f1f0'
  }
};

/**
 * Поверхности, которые antd в тёмной теме красит своими нейтральными серыми
 * (#1f1f1f под поповеры, #1d1d1d под шапку таблицы и ховер строки) — они
 * выбиваются из палитры, особенно из синеватой тёмной нативки.
 *
 * В СВЕТЛОЙ теме дефолты antd оставляем как есть: они там уже вписываются,
 * а подмена делала шапку таблицы и поповеры неправильными.
 */
interface IDarkSurfaces {
  /** --color-surface: поповеры/дропдауны/модалки и шапка таблицы. */
  surface: string;
  /** --color-overlay-hover: ховер строки таблицы (полупрозрачный, ложится на любой фон). */
  hover: string;
}

const WEB_DARK_SURFACES: IDarkSurfaces = {
  surface: '#1a1a1e',
  hover: 'rgba(255, 255, 255, 0.06)'
};

const WEBVIEW_DARK_SURFACES: IDarkSurfaces = {
  surface: '#162033',
  hover: 'rgba(241, 241, 240, 0.06)'
};

/**
 * Таблица рисуется на прозрачном фоне (см. src/shared/ui/table/table.scss),
 * поэтому шапку/подвал кладём на --color-surface, а ховер и подсветку
 * сортированной колонки — на полупрозрачный --color-overlay-hover.
 */
const getDarkOverrides = (surfaces: IDarkSurfaces) => ({
  token: { colorBgElevated: surfaces.surface },
  components: {
    Table: {
      headerBg: surfaces.surface,
      footerBg: surfaces.surface,
      rowHoverBg: surfaces.hover,
      bodySortBg: surfaces.hover
    }
  }
});

export const getAntdTheme = (mode: 'light' | 'dark'): ThemeConfig => {
  const darkTheme = mode === 'dark';
  const inWebView = isInWebView();
  const algorithm = darkTheme
    ? antdTheme.darkAlgorithm
    : antdTheme.defaultAlgorithm;

  const dark = darkTheme
    ? getDarkOverrides(inWebView ? WEBVIEW_DARK_SURFACES : WEB_DARK_SURFACES)
    : null;

  if (inWebView) {
    return {
      algorithm,
      token: {
        ...WEBVIEW_PALETTE[mode],
        ...(dark?.token ?? {})
      },
      components: {
        ...(dark?.components ?? {}),
        // Текст на оранжевом colorPrimary — тёмный (danger-кнопки не трогает).
        Button: {
          primaryColor: '#10192a'
        },
        Steps: {
          colorPrimary: '#176fbd',
          colorPrimaryBg: 'rgba(23, 111, 189, 0.12)'
        }
      }
    };
  }

  return {
    algorithm,
    token: {
      ...WEB_PALETTE,
      colorText: darkTheme ? '#fff' : '#0e0e10',
      ...(dark?.token ?? {})
    },
    components: {
      ...(dark?.components ?? {}),
      Steps: {
        colorPrimary: '#1677ff',
        colorPrimaryBg: 'rgba(43, 143, 229, 0.12)'
      }
    }
  };
};
